import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Link, useHistory } from 'react-router-dom';
import './MachineTestCard.css';
import { translate, getLanguage } from 'react-switch-lang';
import { toast } from 'react-toastify';
import shareIcon from '../assets/icons/share.svg';
import { buildDeepLink, shareDeepLink } from '../utils/deepLink';

const MachineTestCard = ({
  machineTest,
  onAddToCart,
  t,
  className = '',
  onShare,
  ...rest
}) => {
  const [quantity, setQuantity] = useState(1);

  const handleDecrement = () => {
    if (quantity > 1) {
      setQuantity(quantity - 1);
    }
  };

  const handleIncrement = () => {
    setQuantity(quantity + 1);
  };

  const handleAddToCart = () => {
    if (quantity > 0) {
      onAddToCart(machineTest.event_id, quantity, machineTest.topic_id_value);
    }
  };

  const history = useHistory();

  // using shared slugify from utils

  const handleShare = async (e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    try {
      const origin = window && window.location && window.location.origin ? window.location.origin : '';
      const deepLink = buildDeepLink({ origin, eventId: machineTest.event_id });
      // Custom share message for Mind Test
      const testName = machineTest.title || '';
      let shortDesc = '';
      if (machineTest.description) {
        // Remove HTML tags if any
        const stripped = String(machineTest.description).replace(/<[^>]*>/g, '');
        const normalized = stripped.replace(/\s+/g, ' ').trim();
        if (normalized.length <= 100) {
          shortDesc = normalized;
        } else {
          const lastSpace = normalized.lastIndexOf(' ', 100);
          if (lastSpace > 0) {
            shortDesc = normalized.slice(0, lastSpace).trim() + '...';
          } else {
            shortDesc = normalized.slice(0, 100).trim() + '...';
          }
        }
      }
      const shareText = `Check out the Mind Test: "${testName}" on Manashakti Research Centre:\n${shortDesc}\n${deepLink}`;
      const ok = await shareDeepLink({ title: testName, text: shareText, url: deepLink });
      if (ok && onShare) onShare(deepLink);
      return;
    } catch (err) {
      console.error('Share failed', err);
      toast.error('Failed to share link');
    }
  };

  const handleSubtopicClick = (subtopic, subtopicId) => {
    if (!subtopicId) return;

    const topicLabel = machineTest.topicName || machineTest.category_label || machineTest.title || 'Topics';
    const topicIdToUse = machineTest.topic_id_value || machineTest.topicId || null;

    const pathname = topicIdToUse
      ? `/topics/${encodeURIComponent(topicLabel)}/${topicIdToUse}/${subtopicId}`
      : `/topics/${encodeURIComponent(topicLabel)}/${subtopicId}`;

    history.push({
      pathname,
      state: {
        selectedTopic: topicLabel,
        selectedSubtopic: subtopic,
        subtopicId: subtopicId
      }
    });
  };

  // Get the person type label (Couple/Child/Parent/Adult/Person)
  const personTypeLabel = machineTest.first_price_for || 'Person';

  // Check if product is already in cart
  const isInCart = machineTest.added_in_cart && 
    machineTest.added_in_cart.some(cart => cart.cart_id > 0);

  return (
    <article className={`machine-test-card ${className}`.trim()} style={{ position: 'relative' }} {...rest}>
      <button
        type="button"
        onClick={(e) => handleShare(e)}
        aria-label="Share machine test"
        title="Share"
        style={{
          position: 'absolute',
          top: '6px',
          right: '4px',
          background: 'transparent',
          border: 'none',
          padding: 4,
          cursor: 'pointer',
          zIndex: 10
        }}
      >
        <img src={shareIcon} alt="Share" style={{ width: 18, height: 18 }} />
      </button>
      <Link to={`/event_details/${machineTest.event_id}`} className="machine-test-card__image">
        {machineTest.image_name ? (
          <img src={machineTest.image_name} alt={machineTest.title} loading="lazy" />
        ) : (
          <div className="machine-test-card__placeholder">
            <i className="fas fa-clipboard-list" aria-hidden="true"></i>
          </div>
        )}
      </Link>
      
      <div className="machine-test-card__details">
        <h3 className="machine-test-card__name">
          <Link to={`/event_details/${machineTest.event_id}`}>
            {machineTest.title}
          </Link>
        </h3>
        
        {machineTest.category_label && (
          <p className="machine-test-card__category">
            {machineTest.category_label}
          </p>
        )}

        {machineTest.subtopics && machineTest.subtopics.length > 0 && (
          <div className="machine-test-card__subtopics">
            {machineTest.subtopics.slice(0, 2).map((subtopic, index) => {
              const subtopicId = machineTest.subtopicIds && machineTest.subtopicIds.length > index ? machineTest.subtopicIds[index] : null;
              return (
                <React.Fragment key={index}>
                  {index > 0 && <span className="machine-test-card__subtopic-separator"> | </span>}
                  {subtopicId ? (
                    <span
                      className="machine-test-card__subtopic machine-test-card__subtopic--clickable"
                      role="button"
                      tabIndex={0}
                      onClick={(e) => { e.stopPropagation(); handleSubtopicClick(subtopic, subtopicId); }}
                      onKeyPress={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleSubtopicClick(subtopic, subtopicId); } }}
                    >
                      {subtopic}
                    </span>
                  ) : (
                    <span className="machine-test-card__subtopic">{subtopic}</span>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        )}
        
        {machineTest.age_group && (
          <p className="machine-test-card__description">
            {t('home.Age Group')}: {machineTest.age_group}
          </p>
        )}

        {/* Footer pinned to bottom: quantity above actions */}
        <div className="machine-test-card__footer">
          <div className="machine-test-card__quantity">
            <span className="machine-test-card__quantity-label">{personTypeLabel}</span>
            <div className="machine-test-card__quantity-controls">
              <button
                type="button"
                className="machine-test-card__qty-btn"
                onClick={handleDecrement}
                aria-label="Decrease quantity"
              >
                -
              </button>
              <input
                type="number"
                className="machine-test-card__qty-input"
                value={quantity}
                onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                min="1"
                aria-label="Quantity"
              />
              <button
                type="button"
                className="machine-test-card__qty-btn"
                onClick={handleIncrement}
                aria-label="Increase quantity"
              >
                +
              </button>
            </div>
          </div>

          <div className="machine-test-card__actions">
            <div className="machine-test-card__price">
              {t('home.INR')} {machineTest.first_price}/-
            </div>
            <button
              type="button"
              className="machine-test-card__add-btn"
              onClick={handleAddToCart}
            >
              <i className="fas fa-shopping-bag" aria-hidden="true"></i>
              {isInCart ? t('home.ADD MORE') : t('home.ADD TO CART')}
            </button>
          </div>
        </div>
      </div>
    </article>
  );
};

MachineTestCard.propTypes = {
  machineTest: PropTypes.shape({
    event_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    title: PropTypes.string.isRequired,
    topic: PropTypes.arrayOf(PropTypes.shape({
      name: PropTypes.string,
      topic_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      sub_topic_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    })),
    total_review: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    total_like: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    age_group: PropTypes.string,
    first_price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    first_price_for: PropTypes.string,
    topic_id_value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    added_in_cart: PropTypes.array,
  }).isRequired,
  onAddToCart: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  className: PropTypes.string,
  onShare: PropTypes.func,
};

export default translate(MachineTestCard);
